import React, { useEffect, useRef, useState } from "react";
import PencilIcon from "./icons/pencil";
import SidebarGuide from "./side-bar";
import GuideEditor from "./text-editor";
import { Button, Col, Divider, Layout, PageHeader, Row } from "antd";
import styles from "./styles.module.less";
import { useLocation, useNavigate } from "react-router-dom";
import History from "./icons/history";
import EyeIcon from "./icons/eye-icon";
import DashedFile from "./icons/file-dashed";
import TickOutlineIcon from "./icons/tick-outline-icon";
import { getHtmlFromEditor } from "./api/htmlService";
import { getImageURL } from "../../Services/deckleMatchServices";
import { uploadUserGuideImages } from "../../Services/uploadServices";
import {
  updateUserGuide,
  getUserGuideById,
  searchUserGuides,
} from "../../Services/userGuideServices";
import { useUserGuide } from "./user-guide-context.jsx";
import HeaderNav from "../../Components/Header/Header.jsx";
import {
  DetailFields,
  HaberForm,
  OtherPages,
  PageTitleComp,
  RightLayoutContainer,
} from "../styledElements.js";
import DeleteConfirmationModal from "./DeleteConfirmationModal.jsx";
import AlertContainer from "../../Components/AlertBoxes/alertBox";
import alertGreen from "../../Assets/Icons/alertGreen.svg";
import alertRed from "../../Assets/Icons/alertRed.svg";
import SearchDropdown from "./SearchDropdown.jsx";

export default function UserGuide() {
  const { state, handleUserGuideState, resetUserGuideState } = useUserGuide();
  const location = useLocation();
  const navigate = useNavigate();
  const [isEditMode, setIsEditMode] = useState(false);
  const [editAccess, setEditAccess] = useState(null);
  const [editorContent, setEditorContent] = useState("");
  const [plainText, setPlainText] = useState("");
  const selectedItem = state.selectedItem;
  const [openVersions, setOpenVersions] = useState(false);
  const [selectedVersion, setSelectedVersion] = useState(null);
  const isChanged = state.isChanged;
  const [preview, setPreview] = useState(false);
  const [openConfirm, setOpenConfirm] = useState(false);
  const [selectedParam, setSelectedParam] = useState();
  const selectedVersionParam = useRef(null);
  const [isVisibleModal, setIsVisibleModal] = useState(false);
  // Store the full publish_version object when modal opens to prevent versions list from changing during restore
  const [publishVersionForModal, setPublishVersionForModal] = useState(null);
  // Toaster state
  const [actionToaster, setActionToaster] = useState({
    error: false,
    msg: "",
  });
  const userDetails = JSON.parse(localStorage.getItem("user-details"));
  const [visibleToaster, setVisibleToaster] = useState(false);
  // Ref to track the previous selectedItem for saving when navigating away
  const previousSelectedItemRef = useRef(null);

  // Ref to store latest state for auto-save interval (to avoid closure issues)
  const stateRef = useRef(state);

  // Ref to track if content has changed since last save (separate from isChanged for UI)
  const hasUnsavedChangesRef = useRef(false);

  // Update refs whenever state changes
  useEffect(() => {
    stateRef.current = state;
    // Track if content has changed (for auto-save purposes)
    // Only set to true when isChanged becomes true AND we're not switching pages
    // Note: handleEditorChange in text-editor.jsx prevents isChanged from being set during content loading
    const pageChanged =
      previousSelectedItemRef.current?.id !== state.selectedItem?.id;

    if (state.isChanged && !pageChanged) {
      // Only set hasUnsavedChangesRef if isChanged is true AND we haven't just switched pages
      hasUnsavedChangesRef.current = true;
    } else if (!state.isChanged) {
      // Clear hasUnsavedChangesRef when isChanged becomes false
      // This handles the case where isChanged is cleared after content loads
      hasUnsavedChangesRef.current = false;
    }
  }, [state]);

  // Auto-save when navigating away from a page (selectedItem changes or component unmounts)
  useEffect(() => {
    // Save function for the previous page
    const savePreviousPage = async (previousItem) => {
      // Only save if in edit mode, there was a previous page, and there are unsaved changes
      // Add debug logging
      console.log("savePreviousPage called:", {
        isEditMode,
        hasPreviousItem: !!previousItem,
        previousItemId: previousItem?.id,
        isPage: previousItem?.page,
        hasUnsavedChanges: hasUnsavedChangesRef.current,
        isChanged: state.isChanged,
      });

      if (
        isEditMode &&
        previousItem &&
        previousItem.id &&
        previousItem.page &&
        hasUnsavedChangesRef.current
      ) {
        console.log("Auto-saving previous page before navigation");
        try {
          // Get the version
          let currentVersion;
          if (previousItem?.restoredVersion) {
            currentVersion = previousItem.restoredVersion;
          } else {
            currentVersion = getLatestVersion(previousItem?.publish_version);
          }

          // Get the status of the current version
          const currentVersionStatus = getVersionStatus(
            previousItem?.publish_version,
            currentVersion
          );

          // Determine the version to use for draft
          // If current version is "published", increment to create a new version (published -> draft creates new version)
          // If current version is "draft", use the same version
          let version;
          if (currentVersionStatus === "published") {
            // Current version is published, increment to create a new draft version
            version = incrementVersion(currentVersion);
          } else {
            // Current version is draft or unknown, use the same version
            version = currentVersion || "1.0.0";
          }
          const pageId = previousItem.id;

          // Get HTML content from editor
          let htmlContent = getHtmlFromEditor({
            current: { getEditor: () => window.quillRef },
          });

          // Extract and upload images
          const imageData = await extractImagesFromHTML(htmlContent);
          const imageKeyMap = new Map(); // Map original src to image key

          for (const imgData of imageData) {
            if (!imgData || !imgData.file) continue;

            try {
              const bucketKey = `userguide/${pageId}/${imgData.fileName}`;

              const presignedUrl = await getImageURL({
                bucket_key: bucketKey,
                operation: "upload",
              });

              await uploadUserGuideImages(presignedUrl, imgData.file);

              // Map original src to image key (not presigned URL)
              imageKeyMap.set(imgData.originalSrc, bucketKey);
            } catch (error) {
              console.error(
                `Error uploading image ${imgData.fileName}:`,
                error
              );
            }
          }

          // Replace blob/data URLs with image key placeholders
          imageKeyMap.forEach((bucketKey, originalSrc) => {
            const imageKeyPlaceholder = `{{IMAGE_KEY:${bucketKey}}}`;
            htmlContent = htmlContent.replace(
              new RegExp(
                originalSrc.replace(/[.*+?^${}()|[\]\\]/g, "\\$&"),
                "g"
              ),
              imageKeyPlaceholder
            );
          });

          // Also replace any existing presigned URLs with image keys
          htmlContent = replacePresignedUrlsWithKeys(htmlContent, pageId);

          // Upload HTML file to S3
          const htmlFileName = `${pageId}.html`;
          const htmlBucketKey = `user-guide-documents/${pageId}/${version}/${htmlFileName}`;

          const htmlPresignedUrlResponse = await getImageURL({
            bucket_key: htmlBucketKey,
            operation: "upload",
          });

          const htmlBlob = new Blob([htmlContent], { type: "text/html" });
          const htmlFile = new File([htmlBlob], htmlFileName, {
            type: "text/html",
          });

          await uploadUserGuideImages(htmlPresignedUrlResponse, htmlFile);

          // Convert to plain text
          const tempDiv = document.createElement("div");
          tempDiv.innerHTML = htmlContent;
          const plainTextContent =
            tempDiv.textContent || tempDiv.innerText || "";

          // Update via API
          const updatePayload = {
            name: previousItem.title || previousItem.name,
            path: `user-guide-documents/${pageId}/`,
            publish_version: version,
            content: plainTextContent,
            publish_status: "draft",
          };

          await updateUserGuide(pageId, updatePayload);

          // Clear the unsaved changes flag after successful save
          hasUnsavedChangesRef.current = false;
        } catch (error) {
          console.error("Error auto-saving before navigation:", error);
          // Silently fail - don't show toaster for navigation auto-save
        }
      }
    };

    // Check if selectedItem changed (page navigation)
    const pageChanged =
      previousSelectedItemRef.current?.id !== selectedItem?.id;

    if (previousSelectedItemRef.current !== null && pageChanged && isEditMode) {
      // Only save if there are actual unsaved changes
      // Check hasUnsavedChangesRef BEFORE updating it
      if (hasUnsavedChangesRef.current) {
        // Save the previous page before switching (if there are unsaved changes)
        savePreviousPage(previousSelectedItemRef.current);
      }
    }

    // Update ref to current selectedItem
    // Reset hasUnsavedChangesRef when switching to a new page (new page starts with no changes)
    previousSelectedItemRef.current = selectedItem;

    if (pageChanged) {
      // Always reset when switching pages - new page starts with no unsaved changes
      hasUnsavedChangesRef.current = false;
      // Also clear isChanged state when switching pages to prevent false positives
      handleUserGuideState({ isChanged: false });
    }

    // Cleanup: save before unmounting (only on component unmount, not on dependency changes)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedItem?.id, isEditMode, isChanged]);

  // Separate effect for cleanup on unmount only
  useEffect(() => {
    return () => {
      // Save before unmounting if there are unsaved changes
      if (
        isEditMode &&
        previousSelectedItemRef.current &&
        hasUnsavedChangesRef.current
      ) {
        // We can't use async in cleanup, so we'll just log it
        // The auto-save interval should have already saved recent changes
        console.log(
          "Component unmounting - changes should have been auto-saved"
        );
      }
      resetUserGuideState();
    };
  }, []);

  // Helper function to show toaster messages
  const showToaster = (msg, isError = false) => {
    setActionToaster({
      error: isError,
      msg: msg,
    });
    setVisibleToaster(true);
    setTimeout(() => setVisibleToaster(false), 2000);
  };

  const historyRef = useRef(null);
  useEffect(() => {
    const isPreview = preview && location.pathname === "/user-guide/edit";
    setPreview(isPreview);
    // Set window.previewData so QuillTable component can access it
    window.previewData = isPreview;
  }, [location.pathname, preview]);

  // Also update window.previewData when preview state changes
  useEffect(() => {
    const isPreview = preview && location.pathname === "/user-guide/edit";
    window.previewData = isPreview;
    // Dispatch custom event to notify QuillTable components
    window.dispatchEvent(
      new CustomEvent("previewModeChanged", { detail: { preview: isPreview } })
    );
  }, [preview, location.pathname]);
  useEffect(() => {
    setIsEditMode(location.pathname === "/user-guide/edit" && userDetails?.email === "it-support@kaiznn.com");
  }, [location.pathname]);

  // Auto-save functionality: save draft every 60 seconds if there are changes and in edit mode
  useEffect(() => {
    // Only set up auto-save if in edit mode and a page is selected
    if (!isEditMode || !selectedItem?.id || !selectedItem?.page) {
      return;
    }

    const autoSaveInterval = setInterval(async () => {
      // Get current state values from ref (always latest, not from closure)
      const currentState = stateRef.current;
      const currentSelectedItem = currentState.selectedItem;
      const hasUnsavedChanges = hasUnsavedChangesRef.current;

      // Debug log to verify interval is running
      console.log("Auto-save check:", {
        hasUnsavedChanges: hasUnsavedChanges,
        hasItem: !!currentSelectedItem?.id,
        isPage: !!currentSelectedItem?.page,
      });

      // Check if there are unsaved changes (use ref, not isChanged state)
      if (
        hasUnsavedChanges &&
        currentSelectedItem?.id &&
        currentSelectedItem?.page
      ) {
        console.log("Auto-saving...");
        // Call handleSaveDraft silently (without showing toaster for auto-save)
        // We'll call it directly here to avoid dependency issues
        try {
          // Get the version that's currently being viewed/edited
          let currentVersion;
          if (currentSelectedItem?.restoredVersion) {
            currentVersion = currentSelectedItem.restoredVersion;
          } else {
            currentVersion = getLatestVersion(
              currentSelectedItem?.publish_version
            );
          }

          // Get the status of the current version
          const currentVersionStatus = getVersionStatus(
            currentSelectedItem?.publish_version,
            currentVersion
          );

          // Determine the version to use for draft
          // If current version is "published", increment to create a new version (published -> draft creates new version)
          // If current version is "draft", use the same version
          let version;
          if (currentVersionStatus === "published") {
            // Current version is published, increment to create a new draft version
            version = incrementVersion(currentVersion);
          } else {
            // Current version is draft or unknown, use the same version
            version = currentVersion || "1.0.0";
          }
          const pageId = currentSelectedItem.id;

          // Get HTML content from editor
          let htmlContent = getHtmlFromEditor({
            current: { getEditor: () => window.quillRef },
          });

          // Step 1: Extract and upload all images
          const imageData = await extractImagesFromHTML(htmlContent);
          const imageKeyMap = new Map(); // Map original src to image key

          // Upload all images first
          for (const imgData of imageData) {
            if (!imgData || !imgData.file) continue;

            try {
              const bucketKey = `userguide/${pageId}/${imgData.fileName}`;

              const presignedUrl = await getImageURL({
                bucket_key: bucketKey,
                operation: "upload",
              });

              await uploadUserGuideImages(presignedUrl, imgData.file);

              // Map original src to image key (not presigned URL)
              imageKeyMap.set(imgData.originalSrc, bucketKey);
            } catch (error) {
              console.error(
                `Error uploading image ${imgData.fileName}:`,
                error
              );
            }
          }

          // Replace all blob/data URLs in HTML with image key placeholders
          imageKeyMap.forEach((bucketKey, originalSrc) => {
            const imageKeyPlaceholder = `{{IMAGE_KEY:${bucketKey}}}`;
            htmlContent = htmlContent.replace(
              new RegExp(
                originalSrc.replace(/[.*+?^${}()|[\]\\]/g, "\\$&"),
                "g"
              ),
              imageKeyPlaceholder
            );
          });

          // Also replace any existing presigned URLs with image keys
          htmlContent = replacePresignedUrlsWithKeys(htmlContent, pageId);

          // Step 2: Upload HTML file to S3
          const htmlFileName = `${pageId}.html`;
          const htmlBucketKey = `user-guide-documents/${pageId}/${version}/${htmlFileName}`;

          const htmlPresignedUrlResponse = await getImageURL({
            bucket_key: htmlBucketKey,
            operation: "upload",
          });

          const htmlBlob = new Blob([htmlContent], { type: "text/html" });
          const htmlFile = new File([htmlBlob], htmlFileName, {
            type: "text/html",
          });

          await uploadUserGuideImages(htmlPresignedUrlResponse, htmlFile);

          // Step 3: Convert HTML to plain text for content field
          const tempDiv = document.createElement("div");
          tempDiv.innerHTML = htmlContent;
          const plainTextContent =
            tempDiv.textContent || tempDiv.innerText || "";

          // Step 4: Update the user guide via API
          const updatePayload = {
            name: currentSelectedItem.title || currentSelectedItem.name,
            path: `user-guide-documents/${pageId}/`,
            publish_version: version,
            content: plainTextContent,
            publish_status: "draft",
          };

          await updateUserGuide(pageId, updatePayload);

          // Refresh selectedItem with updated data from API
          try {
            const updatedResponse = await getUserGuideById(pageId);
            if (updatedResponse && updatedResponse.data) {
              const updatedPublishVersion =
                typeof updatedResponse.data.publish_version === "object" &&
                !Array.isArray(updatedResponse.data.publish_version)
                  ? updatedResponse.data.publish_version
                  : updatePublishVersionObject(
                      currentSelectedItem?.publish_version,
                      version,
                      "draft"
                    );

              handleUserGuideState({
                selectedItem: {
                  ...currentSelectedItem,
                  ...updatedResponse.data,
                  publish_version: updatedPublishVersion,
                },
                isChanged: false,
              });

              // Clear the unsaved changes flag after successful auto-save
              hasUnsavedChangesRef.current = false;
            }
          } catch (refreshError) {
            console.warn(
              "Failed to refresh selectedItem after auto-save:",
              refreshError
            );
            const updatedPublishVersion = updatePublishVersionObject(
              currentSelectedItem?.publish_version,
              version,
              "draft"
            );
            handleUserGuideState({
              selectedItem: {
                ...currentSelectedItem,
                publish_version: updatedPublishVersion,
              },
              isChanged: false,
            });

            // Clear the unsaved changes flag after auto-save (even if refresh failed)
            hasUnsavedChangesRef.current = false;
          }
        } catch (error) {
          console.error("Error during auto-save:", error);
          // Silently fail for auto-save - don't show toaster
        }
      } else {
        console.log("Auto-save skipped - no changes or invalid state");
      }
    }, 60000); // 60 seconds

    console.log("Auto-save interval set up for page:", selectedItem?.id);

    // Cleanup interval on unmount or when dependencies change
    return () => {
      console.log("Clearing auto-save interval");
      clearInterval(autoSaveInterval);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isEditMode, selectedItem?.id, selectedItem?.page]);

  const handleSaveDraft = async (isAutoSave = false) => {
    if (!selectedItem?.id) {
      showToaster("No document selected", true);
      return;
    }

    // Get the version that's currently being viewed/edited
    // If user restored a specific version, use that; otherwise use the latest version
    let currentVersion;
    if (selectedItem?.restoredVersion) {
      currentVersion = selectedItem.restoredVersion; // User is viewing a restored version
    } else {
      currentVersion = getLatestVersion(selectedItem?.publish_version); // Use latest version
    }

    // Get the status of the current version
    const currentVersionStatus = getVersionStatus(
      selectedItem?.publish_version,
      currentVersion
    );

    // Determine the version to use for draft
    // If current version is "published", increment to create a new version (published -> draft creates new version)
    // If current version is "draft", use the same version
    let version;
    if (currentVersionStatus === "published") {
      // Current version is published, increment to create a new draft version
      version = incrementVersion(currentVersion);
    } else {
      // Current version is draft or unknown, use the same version
      version = currentVersion || "1.0.0";
    }
    const pageId = selectedItem.id;

    try {
      // Show info message (optional - can be removed if not needed)
      // showToaster("Uploading images and content...", false);

      // Get HTML content from editor
      let htmlContent = getHtmlFromEditor({
        current: { getEditor: () => window.quillRef },
      });

      // Step 1: Extract and upload all images
      const imageData = await extractImagesFromHTML(htmlContent);
      const imageKeyMap = new Map(); // Map original src to image key (bucket key)

      // Upload all images first
      for (const imgData of imageData) {
        if (!imgData || !imgData.file) continue;

        try {
          const bucketKey = `userguide/${pageId}/${imgData.fileName}`;

          // Get presigned URL for image upload
          const presignedUrl = await getImageURL({
            bucket_key: bucketKey,
            operation: "upload",
          });

          // Upload image to S3
          await uploadUserGuideImages(presignedUrl, imgData.file);

          // Map original src to image key (not presigned URL)
          imageKeyMap.set(imgData.originalSrc, bucketKey);
        } catch (error) {
          console.error(`Error uploading image ${imgData.fileName}:`, error);
          // Continue with other images even if one fails
        }
      }

      // Replace all blob/data URLs in HTML with image key placeholders
      imageKeyMap.forEach((bucketKey, originalSrc) => {
        const imageKeyPlaceholder = `{{IMAGE_KEY:${bucketKey}}}`;
        htmlContent = htmlContent.replace(
          new RegExp(originalSrc.replace(/[.*+?^${}()|[\]\\]/g, "\\$&"), "g"),
          imageKeyPlaceholder
        );
      });

      // Also replace any existing presigned URLs with image keys
      htmlContent = replacePresignedUrlsWithKeys(htmlContent, pageId);

      // Step 2: Upload HTML file to S3
      const htmlFileName = `${pageId}.html`;
      const htmlBucketKey = `user-guide-documents/${pageId}/${version}/${htmlFileName}`;

      // Get presigned URL for HTML file
      const htmlPresignedUrlResponse = await getImageURL({
        bucket_key: htmlBucketKey,
        operation: "upload",
      });

      const htmlPresignedUrl = htmlPresignedUrlResponse;

      // Convert HTML content to Blob
      const htmlBlob = new Blob([htmlContent], { type: "text/html" });
      const htmlFile = new File([htmlBlob], htmlFileName, {
        type: "text/html",
      });

      // Upload HTML file to S3
      await uploadUserGuideImages(htmlPresignedUrl, htmlFile);

      // Step 3: Convert HTML to plain text for content field
      // Create a temporary DOM element to extract plain text
      const tempDiv = document.createElement("div");
      tempDiv.innerHTML = htmlContent;
      const plainTextContent = tempDiv.textContent || tempDiv.innerText || "";

      // Step 4: Update the user guide via API (without version increment, with draft status)
      const updatePayload = {
        name: selectedItem.title || selectedItem.name,
        path: `user-guide-documents/${pageId}/`,
        publish_version: version, // Use same version, don't increment
        content: plainTextContent, // Send plain text without HTML tags
        publish_status: "draft", // Set publish_status to 'draft'
      };

      await updateUserGuide(pageId, updatePayload);

      // Refresh selectedItem with updated data from API
      try {
        const updatedResponse = await getUserGuideById(pageId);
        if (updatedResponse && updatedResponse.data) {
          // Preserve publish_version as object - API returns object, use it directly
          const updatedPublishVersion =
            typeof updatedResponse.data.publish_version === "object" &&
            !Array.isArray(updatedResponse.data.publish_version)
              ? updatedResponse.data.publish_version
              : updatePublishVersionObject(
                  selectedItem?.publish_version,
                  version,
                  "draft"
                );

          handleUserGuideState({
            selectedItem: {
              ...selectedItem,
              ...updatedResponse.data,
              publish_version: updatedPublishVersion, // Keep as object
            },
            isChanged: false,
          });
        }
      } catch (refreshError) {
        console.warn(
          "Failed to refresh selectedItem after save draft:",
          refreshError
        );
        // Update selectedItem with draft status even if refresh fails - preserve as object
        const updatedPublishVersion = updatePublishVersionObject(
          selectedItem?.publish_version,
          version,
          "draft"
        );
        handleUserGuideState({
          selectedItem: {
            ...selectedItem,
            publish_version: updatedPublishVersion, // Keep as object
          },
          isChanged: false,
        });
      }

      handleUserGuideState({ isChanged: false });

      // Clear the unsaved changes flag after successful save
      hasUnsavedChangesRef.current = false;

      // Only show toaster for manual saves, not auto-saves
      if (!isAutoSave) {
        showToaster("Draft saved successfully", false);
      }
    } catch (error) {
      console.error("Error saving draft:", error);
      // Only show error toaster for manual saves, not auto-saves
      if (!isAutoSave) {
        showToaster(
          error.message || "Failed to save draft. Please try again.",
          true
        );
      }
    }
  };
  // Helper function to extract images from HTML and convert blob URLs to files
  // Helper function to replace presigned URLs with image keys in HTML
  const replaceImageUrlsWithKeys = (htmlContent, pageId) => {
    const parser = new DOMParser();
    const doc = parser.parseFromString(htmlContent, "text/html");
    const images = doc.querySelectorAll("img[src]");

    images.forEach((img) => {
      const src = img.getAttribute("src");
      // Check if it's a presigned URL (contains query parameters or is an S3 URL)
      // If it's already an image key (starts with userguide/), leave it as is
      if (
        src &&
        !src.startsWith("userguide/") &&
        !src.startsWith("blob:") &&
        !src.startsWith("data:")
      ) {
        // Extract bucket key from presigned URL or use a pattern to identify it
        // For now, we'll extract the image key from existing images
        // If the image is already uploaded, we need to extract the key from the URL
        // For new images, we'll handle them in the upload process
        const bucketKeyMatch = src.match(/userguide\/(\d+)\/([^?]+)/);
        if (bucketKeyMatch) {
          const bucketKey = `userguide/${bucketKeyMatch[1]}/${bucketKeyMatch[2]}`;
          img.setAttribute("src", `{{IMAGE_KEY:${bucketKey}}}`);
        }
      }
    });

    return doc.documentElement.outerHTML;
  };

  // Helper function to replace image keys with fresh presigned URLs
  const extractImagesFromHTML = (htmlContent) => {
    const parser = new DOMParser();
    const doc = parser.parseFromString(htmlContent, "text/html");
    const images = doc.querySelectorAll("img[src]");
    const imagePromises = [];

    images.forEach((img, index) => {
      const src = img.getAttribute("src");

      // Check if it's a blob URL
      if (src.startsWith("blob:")) {
        imagePromises.push(
          fetch(src)
            .then((res) => res.blob())
            .then((blob) => {
              // Get file extension from blob type or default to png
              const extension = blob.type.split("/")[1] || "png";
              const fileName = `image_${Date.now()}_${index}.${extension}`;
              const file = new File([blob], fileName, { type: blob.type });
              return {
                element: img,
                file: file,
                fileName: fileName,
                originalSrc: src,
              };
            })
            .catch((err) => {
              console.error("Error converting blob to file:", err);
              return null;
            })
        );
      } else if (src.startsWith("data:")) {
        // Handle base64 images
        const base64Data = src.split(",")[1];
        const mimeType = src.match(/data:([^;]+);/)?.[1] || "image/png";
        const extension = mimeType.split("/")[1] || "png";
        const fileName = `image_${Date.now()}_${index}.${extension}`;
        const byteCharacters = atob(base64Data);
        const byteNumbers = new Array(byteCharacters.length);
        for (let i = 0; i < byteCharacters.length; i++) {
          byteNumbers[i] = byteCharacters.charCodeAt(i);
        }
        const byteArray = new Uint8Array(byteNumbers);
        const file = new File([byteArray], fileName, { type: mimeType });
        imagePromises.push(
          Promise.resolve({
            element: img,
            file: file,
            fileName: fileName,
            originalSrc: src,
          })
        );
      }
    });

    return Promise.all(imagePromises.filter(Boolean));
  };

  // Helper function to replace existing presigned URLs with image keys in HTML
  const replacePresignedUrlsWithKeys = (htmlContent, pageId) => {
    const parser = new DOMParser();
    const doc = parser.parseFromString(htmlContent, "text/html");
    const images = doc.querySelectorAll("img[src]");
    let hasChanges = false;

    images.forEach((img) => {
      const src = img.getAttribute("src");
      // Check if it's a presigned URL (contains query parameters or is an S3 URL)
      // but not already an image key placeholder, blob, or data URL
      if (
        src &&
        !src.startsWith("{{IMAGE_KEY:") &&
        !src.startsWith("blob:") &&
        !src.startsWith("data:") &&
        (src.includes("?") ||
          src.includes("amazonaws.com") ||
          src.includes("s3") ||
          src.startsWith("http"))
      ) {
        // Try to extract bucket key from the URL
        // Pattern: userguide/{pageId}/{fileName} or user-guide-documents/...
        const bucketKeyMatch = src.match(/userguide\/(\d+)\/([^?&]+)/);
        if (bucketKeyMatch) {
          const bucketKey = `userguide/${bucketKeyMatch[1]}/${bucketKeyMatch[2]}`;
          const imageKeyPlaceholder = `{{IMAGE_KEY:${bucketKey}}}`;
          img.setAttribute("src", imageKeyPlaceholder);
          hasChanges = true;
        }
      }
    });

    return hasChanges ? doc.documentElement.outerHTML : htmlContent;
  };

  /**
   * Generate array of all versions from 1.0.0 to current version
   * Assumes versions follow semantic versioning with patch increments
   * @param {string} currentVersion - Current version string (e.g., "1.0.2")
   * @returns {Array<string>} Array of versions ["1.0.0", "1.0.1", "1.0.2"]
   */
  /**
   * Generate array of versions from publish_version object
   * @param {Object|string} publishVersion - publish_version object (e.g., { "1.0.0": "draft", "1.0.1": "published" }) or version string
   * @returns {Array<string>} Array of version strings sorted by version number
   */
  const generateVersionsArray = (publishVersion) => {
    if (!publishVersion) {
      return ["1.0.0"];
    }

    // Handle object structure: { "1.0.0": "draft", "1.0.1": "published" }
    if (typeof publishVersion === "object" && !Array.isArray(publishVersion)) {
      const versions = Object.keys(publishVersion).sort((a, b) => {
        // Sort versions numerically
        const aParts = a.split(".").map(Number);
        const bParts = b.split(".").map(Number);
        for (let i = 0; i < Math.max(aParts.length, bParts.length); i++) {
          const aVal = aParts[i] || 0;
          const bVal = bParts[i] || 0;
          if (aVal !== bVal) return aVal - bVal;
        }
        return 0;
      });
      return versions.length > 0 ? versions : ["1.0.0"];
    }

    // Fallback for string (backward compatibility)
    if (typeof publishVersion === "string") {
      const versionParts = publishVersion
        .split(".")
        .map((part) => parseInt(part, 10));
      const major = versionParts[0] || 1;
      const minor = versionParts[1] || 0;
      const patch = versionParts[2] || 0;

      const versions = [];
      for (let p = 0; p <= patch; p++) {
        versions.push(`${major}.${minor}.${p}`);
      }
      return versions.length > 0 ? versions : ["1.0.0"];
    }

    // Fallback for array (backward compatibility)
    if (Array.isArray(publishVersion) && publishVersion.length > 0) {
      return publishVersion;
    }

    return ["1.0.0"];
  };

  /**
   * Get the latest version from publish_version object
   * @param {Object|string|Array<string>} publishVersion - publish_version object, string, or array
   * @returns {string} Latest version string
   */
  const getLatestVersion = (publishVersion) => {
    // Handle object structure: { "1.0.0": "draft", "1.0.1": "published" }
    if (typeof publishVersion === "object" && !Array.isArray(publishVersion)) {
      const versions = Object.keys(publishVersion);
      if (versions.length === 0) {
        return "1.0.0";
      }
      // Sort and get the latest version
      const sortedVersions = versions.sort((a, b) => {
        const aParts = a.split(".").map(Number);
        const bParts = b.split(".").map(Number);
        for (let i = 0; i < Math.max(aParts.length, bParts.length); i++) {
          const aVal = aParts[i] || 0;
          const bVal = bParts[i] || 0;
          if (aVal !== bVal) return aVal - bVal;
        }
        return 0;
      });
      return sortedVersions[sortedVersions.length - 1];
    }

    // Fallback for array or string (backward compatibility)
    if (Array.isArray(publishVersion) && publishVersion.length > 0) {
      return publishVersion[publishVersion.length - 1];
    }
    if (typeof publishVersion === "string" && publishVersion) {
      return publishVersion;
    }
    return "1.0.0";
  };

  /**
   * Get the status of a specific version from publish_version object
   * @param {Object|string|Array<string>} publishVersion - publish_version object, string, or array
   * @param {string} version - Version string to check (e.g., "1.0.0")
   * @returns {string} Status of the version ("draft", "published", or "unknown")
   */
  const getVersionStatus = (publishVersion, version) => {
    // Handle object structure:
    // New format: { "1.0.0": {status: "draft", published_at: "2025-12-24 05:02:34"} }
    // Old format: { "1.0.0": "draft" }
    if (typeof publishVersion === "object" && !Array.isArray(publishVersion)) {
      const versionData = publishVersion[version];
      if (!versionData) return "unknown";

      // Check if it's the new format (object with status property)
      if (typeof versionData === "object" && versionData.status) {
        return versionData.status;
      }
      // Old format (direct string value)
      if (typeof versionData === "string") {
        return versionData;
      }
      return "unknown";
    }

    // For array or string, assume "published" (backward compatibility)
    return "published";
  };

  /**
   * Update publish_version object with new version and status
   * @param {Object|string|Array<string>} currentPublishVersion - Current version (object, array, or string)
   * @param {string} newVersion - New version to add
   * @param {string} status - Status for the version ("draft" or "published")
   * @returns {Object} Updated publish_version object
   */
  const updatePublishVersionObject = (
    currentPublishVersion,
    newVersion,
    status = "published"
  ) => {
    let versionObject = {};

    // Handle object structure
    if (
      typeof currentPublishVersion === "object" &&
      !Array.isArray(currentPublishVersion)
    ) {
      // Copy existing versions, preserving their structure (new or old format)
      versionObject = { ...currentPublishVersion };
    } else if (Array.isArray(currentPublishVersion)) {
      // Convert array to object (backward compatibility)
      currentPublishVersion.forEach((version) => {
        versionObject[version] = { status: "published", published_at: null }; // Default to new format
      });
    } else if (
      typeof currentPublishVersion === "string" &&
      currentPublishVersion
    ) {
      // Convert string to object (backward compatibility)
      versionObject[currentPublishVersion] = {
        status: "published",
        published_at: null,
      };
    } else {
      versionObject = { "1.0.0": { status: "draft", published_at: null } };
    }

    // Add or update the new version with status in new format
    // Format: {status: "draft"/"published", published_at: "YYYY-MM-DD HH:mm:ss" or null}
    const now = new Date();
    const publishedAt =
      status === "published"
        ? now.toISOString().slice(0, 19).replace("T", " ") // Format: "2025-12-24 05:02:34"
        : null;

    // Check if existing version has old format (string) and convert it
    const existingVersion = versionObject[newVersion];
    if (typeof existingVersion === "string") {
      // Convert old format to new format
      versionObject[newVersion] = {
        status: status, // Use new status
        published_at:
          status === "published"
            ? publishedAt
            : existingVersion === "published"
            ? null
            : null,
      };
    } else if (
      existingVersion &&
      typeof existingVersion === "object" &&
      existingVersion.status
    ) {
      // Already in new format, update it
      versionObject[newVersion] = {
        status: status,
        published_at:
          status === "published" ? publishedAt : existingVersion.published_at,
      };
    } else {
      // New version, create in new format
      versionObject[newVersion] = {
        status: status,
        published_at: publishedAt,
      };
    }

    return versionObject;
  };

  /**
   * Increment semantic version (patch version)
   * Examples: "1.0.0" → "1.0.1", "1.0.1" → "1.0.2", "2.1.5" → "2.1.6"
   * @param {string} currentVersion - Current version string (e.g., "1.0.0")
   * @returns {string} Incremented version string
   */
  const incrementVersion = (currentVersion) => {
    if (!currentVersion || typeof currentVersion !== "string") {
      return "1.0.0";
    }

    // Parse version string (e.g., "1.0.0" or "1.0")
    const versionParts = currentVersion.split(".").map((part) => {
      const num = parseInt(part, 10);
      return isNaN(num) ? 0 : num;
    });

    // Ensure we have at least 3 parts (major.minor.patch)
    while (versionParts.length < 3) {
      versionParts.push(0);
    }

    // Increment patch version (last number)
    versionParts[versionParts.length - 1] += 1;

    return versionParts.join(".");
  };

  const handlePublishDoc = async () => {
    if (!selectedItem?.id) {
      showToaster("No document selected", true);
      return;
    }

    // Get the version that's currently being viewed/edited
    // If user restored a specific version, use that; otherwise use the latest version
    let currentVersion;
    if (selectedItem?.restoredVersion) {
      currentVersion = selectedItem.restoredVersion; // User is viewing a restored version
    } else {
      currentVersion = getLatestVersion(selectedItem?.publish_version); // Use latest version
    }

    // Get the status of the current version
    const currentVersionStatus = getVersionStatus(
      selectedItem?.publish_version,
      currentVersion
    );

    // Determine the version to use for publishing
    // If current version is "draft", use the same version (just change status to "published")
    // If current version is "published", increment to create a new version
    let version;
    if (currentVersionStatus === "draft") {
      version = currentVersion; // Use same version, just update status to "published"
    } else {
      // Current version is already published, increment to create a new version
      version = incrementVersion(currentVersion);
    }

    const pageId = selectedItem.id;

    try {
      // Show info message (optional - can be removed if not needed)
      // showToaster("Uploading images and content...", false);

      // Get HTML content from editor
      let htmlContent = getHtmlFromEditor({
        current: { getEditor: () => window.quillRef },
      });

      // Step 1: Extract and upload all images
      const imageData = await extractImagesFromHTML(htmlContent);
      const imageKeyMap = new Map(); // Map original src to image key (bucket key)

      // Upload all images first
      for (const imgData of imageData) {
        if (!imgData || !imgData.file) continue;

        try {
          const bucketKey = `userguide/${pageId}/${imgData.fileName}`;

          // Get presigned URL for image upload
          const presignedUrl = await getImageURL({
            bucket_key: bucketKey,
            operation: "upload",
          });

          // Upload image to S3
          await uploadUserGuideImages(presignedUrl, imgData.file);

          // Map original src to image key (not presigned URL)
          imageKeyMap.set(imgData.originalSrc, bucketKey);
        } catch (error) {
          console.error(`Error uploading image ${imgData.fileName}:`, error);
          // Continue with other images even if one fails
        }
      }

      // Replace all blob/data URLs in HTML with image key placeholders
      imageKeyMap.forEach((bucketKey, originalSrc) => {
        const imageKeyPlaceholder = `{{IMAGE_KEY:${bucketKey}}}`;
        htmlContent = htmlContent.replace(
          new RegExp(originalSrc.replace(/[.*+?^${}()|[\]\\]/g, "\\$&"), "g"),
          imageKeyPlaceholder
        );
      });

      // Also replace any existing presigned URLs with image keys
      htmlContent = replacePresignedUrlsWithKeys(htmlContent, pageId);

      // Step 2: Upload HTML file to S3
      const htmlFileName = `${pageId}.html`;
      const htmlBucketKey = `user-guide-documents/${pageId}/${version}/${htmlFileName}`;

      // Get presigned URL for HTML file
      const htmlPresignedUrlResponse = await getImageURL({
        bucket_key: htmlBucketKey,
        operation: "upload",
      });

      const htmlPresignedUrl = htmlPresignedUrlResponse;

      // Convert HTML content to Blob
      const htmlBlob = new Blob([htmlContent], { type: "text/html" });
      const htmlFile = new File([htmlBlob], htmlFileName, {
        type: "text/html",
      });

      // Upload HTML file to S3
      await uploadUserGuideImages(htmlPresignedUrl, htmlFile);

      // Step 3: Convert HTML to plain text for content field
      // Create a temporary DOM element to extract plain text
      const tempDiv = document.createElement("div");
      tempDiv.innerHTML = htmlContent;
      const plainTextContent = tempDiv.textContent || tempDiv.innerText || "";

      // Step 4: Update the user guide via API
      const updatePayload = {
        name: selectedItem.title || selectedItem.name,
        path: `user-guide-documents/${pageId}/`,
        publish_version: version,
        content: plainTextContent, // Send plain text without HTML tags
        publish_status: "publish", // Set publish_status to 'publish' when publishing
      };

      await updateUserGuide(pageId, updatePayload);

      // Refresh selectedItem with updated version from API
      try {
        const updatedResponse = await getUserGuideById(pageId);
        if (updatedResponse && updatedResponse.data) {
          // Preserve publish_version as object - API returns object, use it directly
          const updatedPublishVersion =
            typeof updatedResponse.data.publish_version === "object" &&
            !Array.isArray(updatedResponse.data.publish_version)
              ? updatedResponse.data.publish_version
              : updatePublishVersionObject(
                  selectedItem?.publish_version,
                  version,
                  "published"
                );

          handleUserGuideState({
            selectedItem: {
              ...selectedItem,
              ...updatedResponse.data,
              publish_version: updatedPublishVersion, // Keep as object
            },
            isChanged: false,
          });
        }
      } catch (refreshError) {
        console.warn(
          "Failed to refresh selectedItem after publish:",
          refreshError
        );
        // Update selectedItem with new version even if refresh fails - preserve as object
        const updatedPublishVersion = updatePublishVersionObject(
          selectedItem?.publish_version,
          version,
          "published"
        );
        handleUserGuideState({
          selectedItem: {
            ...selectedItem,
            publish_version: updatedPublishVersion, // Keep as object
          },
          isChanged: false,
        });
      }

      // Clear the unsaved changes flag after successful publish
      hasUnsavedChangesRef.current = false;

      showToaster(
        `Document has been successfully published as version ${version}`,
        false
      );
    } catch (error) {
      console.error("Error publishing document:", error);
      showToaster(
        error.message || "Failed to publish document. Please try again.",
        true
      );
    }
  };
  const handleRestore = async (versionItem) => {
    console.log("versionItem", versionItem);

    if (!selectedItem?.id || !versionItem?.version) {
      showToaster("Unable to restore version", true);
      return;
    }

    // Auto-save current page before restoring version if there are changes
    if (isEditMode && selectedItem?.page && hasUnsavedChangesRef.current) {
      try {
        // Get the version
        let currentVersion;
        if (selectedItem?.restoredVersion) {
          currentVersion = selectedItem.restoredVersion;
        } else {
          currentVersion = getLatestVersion(selectedItem?.publish_version);
        }

        const version = currentVersion || "1.0.0";
        const pageId = selectedItem.id;

        // Get HTML content from editor
        let htmlContent = getHtmlFromEditor({
          current: { getEditor: () => window.quillRef },
        });

        // Extract and upload images
        const imageData = await extractImagesFromHTML(htmlContent);
        const imageKeyMap = new Map(); // Map original src to image key

        for (const imgData of imageData) {
          if (!imgData || !imgData.file) continue;

          try {
            const bucketKey = `userguide/${pageId}/${imgData.fileName}`;

            const presignedUrl = await getImageURL({
              bucket_key: bucketKey,
              operation: "upload",
            });

            await uploadUserGuideImages(presignedUrl, imgData.file);

            // Map original src to image key (not presigned URL)
            imageKeyMap.set(imgData.originalSrc, bucketKey);
          } catch (error) {
            console.error(`Error uploading image ${imgData.fileName}:`, error);
          }
        }

        // Replace blob/data URLs with image key placeholders
        imageKeyMap.forEach((bucketKey, originalSrc) => {
          const imageKeyPlaceholder = `{{IMAGE_KEY:${bucketKey}}}`;
          htmlContent = htmlContent.replace(
            new RegExp(originalSrc.replace(/[.*+?^${}()|[\]\\]/g, "\\$&"), "g"),
            imageKeyPlaceholder
          );
        });

        // Also replace any existing presigned URLs with image keys
        htmlContent = replacePresignedUrlsWithKeys(htmlContent, pageId);

        // Upload HTML file to S3
        const htmlFileName = `${pageId}.html`;
        const htmlBucketKey = `user-guide-documents/${pageId}/${version}/${htmlFileName}`;

        const htmlPresignedUrlResponse = await getImageURL({
          bucket_key: htmlBucketKey,
          operation: "upload",
        });

        const htmlBlob = new Blob([htmlContent], { type: "text/html" });
        const htmlFile = new File([htmlBlob], htmlFileName, {
          type: "text/html",
        });

        await uploadUserGuideImages(htmlPresignedUrlResponse, htmlFile);

        // Convert to plain text
        const tempDiv = document.createElement("div");
        tempDiv.innerHTML = htmlContent;
        const plainTextContent = tempDiv.textContent || tempDiv.innerText || "";

        // Update via API
        const updatePayload = {
          name: selectedItem.title || selectedItem.name,
          path: `user-guide-documents/${pageId}/`,
          publish_version: version,
          content: plainTextContent,
          publish_status: "draft",
        };

        await updateUserGuide(pageId, updatePayload);

        // Clear flags after saving
        handleUserGuideState({ isChanged: false });
        hasUnsavedChangesRef.current = false;
      } catch (error) {
        console.error("Error auto-saving before version restore:", error);
        // Continue with restore even if auto-save fails
      }
    }

    try {
      // Update selectedItem with the selected version
      // Keep publish_version as object, but we're viewing a specific version
      // The object should remain intact for the versions list
      // New format: { "1.0.0": {status: "draft", published_at: "2025-12-24 05:02:34"} }
      // Old format: { "1.0.0": "draft" }
      const currentPublishVersion =
        typeof selectedItem?.publish_version === "object" &&
        !Array.isArray(selectedItem?.publish_version)
          ? { ...selectedItem.publish_version } // Copy object (preserves new or old format)
          : selectedItem?.publish_version
          ? {
              [selectedItem.publish_version]: {
                status: "published",
                published_at: null,
              },
            }
          : { "1.0.0": { status: "draft", published_at: null } };

      // Keep the object as-is (don't modify it when restoring)
      // The text editor will use the latest version or the restored version
      handleUserGuideState({
        selectedItem: {
          ...selectedItem,
          publish_version: currentPublishVersion, // Keep as object
          // Store the restored version separately for reference
          restoredVersion: versionItem.version,
        },
      });

      setIsVisibleModal(false);
      setOpenConfirm(false);
      setSelectedParam(undefined);
      selectedVersionParam.current = undefined;
      // Don't reset latestVersionForModal here - keep it so versions list stays consistent

      showToaster(`Document restored to version ${versionItem.version}`, false);
    } catch (error) {
      console.error("Error restoring version:", error);
      showToaster("Failed to restore version. Please try again.", true);
    }
  };
  const handleSelect = async (item) => {
    try {
      // Fetch the document by ID to get full details
      if (item.document_id) {
        const response = await getUserGuideById(item.document_id);
        if (response && response.data) {
          const documentData = response.data;

          // Transform API response to match sidebar structure
          const selectedDocument = {
            id: documentData.id,
            uuid: documentData.uuid,
            title: documentData.name,
            name: documentData.name,
            page: documentData.type === "page",
            path: documentData.path,
            content: documentData.content,
            docUrl: documentData.path,
            publish_version: documentData.publish_version,
            created_at: documentData.created_at,
            updated_at: documentData.updated_at,
            parent_id: documentData.parent_id,
            icon: documentData.icon,
          };

          // Set the selected item in the context
          handleUserGuideState({ selectedItem: selectedDocument });
        }
      }
    } catch (error) {
      console.error("Error loading selected document:", error);
      showToaster("Failed to load document", true);
    }
  };

  const handleSearch = async (query) => {
    if (!query || query.trim().length === 0) {
      return [];
    }

    try {
      const response = await searchUserGuides(query.trim());

      if (response && response.data && response.data.searchResult) {
        const flatResults = [];

        // Transform API response to match SearchDropdown expected format
        response.data.searchResult.forEach((module) => {
          if (module.search_result && Array.isArray(module.search_result)) {
            module.search_result.forEach((doc) => {
              flatResults.push({
                id: doc.document_id,
                label: doc.document_title,
                section: doc.hir_path,
                module: module.module_name,
                // Don't include url - we handle navigation through context
                document_id: doc.document_id,
                document_path: doc.document_path,
                headline: doc.headline,
                version: doc.version,
                parent_module: doc.parent_module,
              });
            });
          }
        });

        return flatResults;
      }

      return [];
    } catch (error) {
      console.error("Error searching user guides:", error);
      showToaster("Failed to search documents", true);
      return [];
    }
  };

  return (
    <>
      <Layout>
        <HeaderNav />
        <RightLayoutContainer>
          <OtherPages>
            <DetailFields>
              <HaberForm>
                <Layout
                  style={{ backgroundColor: "#ffffff", paddingTop: "0px" }}
                >
                  <PageTitleComp
                    style={{ backgroundColor: "#ffffff", border: "none" }}
                  >
                    <PageHeader
                      style={{ backgroundColor: "#ffffff", border: "none" }}
                      ghost={false}
                      title="User Guide"
                      extra={
                        isEditMode && selectedItem?.page && !preview && userDetails?.email === "it-support@kaiznn.com"
                          ? [
                              <Button
                                key="history"
                                className="themeBtn noBg"
                                onClick={() => {
                                  // Store the latest version before opening modal
                                  // This ensures the versions list doesn't change when restoring
                                  // Store the full publish_version object when opening modal
                                  const currentPublishVersion =
                                    selectedItem?.publish_version;
                                  setPublishVersionForModal(
                                    currentPublishVersion
                                  );
                                  setOpenVersions(true);
                                  setIsVisibleModal(true);
                                }}
                              >
                                <div ref={historyRef}>
                                  <History color="#ea655b" />
                                </div>
                              </Button>,
                              <Button
                                key="preview"
                                className="themeBtn noBg"
                                onClick={() => setPreview(true)}
                                disabled={userDetails?.email !== "it-support@kaiznn.com"}
                              >
                                <div
                                  style={{
                                    display: "flex",
                                    flexDirection: "row",
                                    alignItems: "center",
                                    color: "#ea655b",
                                  }}
                                >
                                  <div style={{ paddingRight: "8px" }}>
                                    <EyeIcon color="#ea655b" />
                                  </div>
                                  Preview changes
                                </div>
                              </Button>,
                              <Button
                                key="draft"
                                className="themeBtn noBg"
                                onClick={handleSaveDraft}
                                disabled={isChanged && userDetails?.email !== "it-support@kaiznn.com"}
                              >
                                <div
                                  style={{
                                    display: "flex",
                                    flexDirection: "row",
                                    color: "#ea655b",
                                  }}
                                >
                                  <div style={{ paddingRight: "8px" }}>
                                    <DashedFile color="#ea655b" />
                                  </div>
                                  Save draft
                                </div>
                              </Button>,
                              <Button
                                key="publish"
                                className="themeBtn"
                                type="primary"
                                disabled={isChanged && userDetails?.email !== "it-support@kaiznn.com"}
                                onClick={handlePublishDoc}
                              >
                                <div
                                  style={{
                                    display: "flex",
                                    flexDirection: "row",
                                    alignItems: "center",
                                  }}
                                >
                                  <div style={{ paddingRight: "8px" }}>
                                    <TickOutlineIcon color="#FFF" />
                                  </div>
                                  Publish
                                </div>
                              </Button>,
                            ]
                          : preview && selectedItem?.page
                          ? [
                              <Button
                                key="back"
                                className="themeBtn noBg"
                                onClick={() => setPreview(false)}
                                disabled={userDetails?.email !== "it-support@kaiznn.com"}
                              >
                                <div
                                  style={{
                                    display: "flex",
                                    flexDirection: "row",
                                    alignItems: "center",
                                    color: "#ea655b",
                                  }}
                                >
                                  <div style={{ paddingRight: "8px" }}></div>
                                  Back to Editor
                                </div>
                              </Button>,
                              <Button
                                key="draft"
                                className="themeBtn noBg"
                                onClick={handleSaveDraft}
                                disabled={userDetails?.email !== "it-support@kaiznn.com"}
                              >
                                <div
                                  style={{
                                    display: "flex",
                                    flexDirection: "row",
                                    alignItems: "center",
                                    color: "#ea655b",
                                  }}
                                >
                                  <div style={{ paddingRight: "8px" }}>
                                    <DashedFile color="#ea655b" />
                                  </div>
                                  Save draft
                                </div>
                              </Button>,
                              <Button
                                key="publish"
                                className="themeBtn"
                                type="primary"
                                disabled={isChanged &&  userDetails?.email !== "it-support@kaiznn.com"
                                }
                                onClick={handlePublishDoc}
                              >
                                <div
                                  style={{
                                    display: "flex",
                                    flexDirection: "row",
                                    alignItems: "center",
                                  }}
                                >
                                  <div style={{ paddingRight: "8px" }}>
                                    <TickOutlineIcon color="#FFF" />
                                  </div>
                                  Publish
                                </div>
                              </Button>,
                            ]
                          : !isEditMode &&
                            !preview &&
                            editAccess &&
                            userDetails?.email === "it-support@kaiznn.com"
                          ? [
                              <Button
                                key="edit"
                                className="themeBtn"
                                type="primary"
                                onClick={() => navigate(`/user-guide/edit`)}
                              >
                                <div
                                  style={{
                                    display: "flex",
                                    flexDirection: "row",
                                    alignItems: "center",
                                  }}
                                >
                                  <div style={{ paddingRight: "8px" }}>
                                    <PencilIcon />
                                  </div>
                                  Edit
                                </div>
                              </Button>,
                            ]
                          : []
                      }
                    />
                  </PageTitleComp>
                  <div
                    style={{
                      position: "absolute",
                      width: "28%",
                      left: "28%",
                      padding: "16px 0px",
                    }}
                  >
                    <SearchDropdown
                      onSearch={handleSearch}
                      onSelect={handleSelect}
                      placeholder="Search documentation..."
                    />
                  </div>
                </Layout>

                {preview && (
                  <p
                    style={{
                      fontSize: 14,
                      //   fontFamily: "Space Grotesk",
                      color: "gray",
                      fontWeight: 500,
                      transform: "translate(0%,-50%)",
                    }}
                  >
                    Preview
                  </p>
                )}
                <Divider className={styles.divider} />
                <Row
                  id="editor"
                  gutter={[4, 4]}
                  style={{ height: "95vh", margin: 0, flexWrap: "nowrap" }}
                >
                  <Col span={5}>
                    <div
                      style={{
                        display: "flex",
                        flexDirection: "row",
                        justifyContent: "space-between",
                        height: "100%",
                      }}
                    >
                      <SidebarGuide
                        edit={isEditMode}
                        onEditAccessChange={setEditAccess}
                        preview={preview}
                        showToaster={showToaster}
                      />
                      <div
                        style={{
                          width: "1px",
                          backgroundColor: "#f0f0f0",
                          margin: "0 8px",
                          height: "95vh",
                        }}
                      />
                    </div>
                  </Col>
                  <Col span={19}>
                    <GuideEditor
                      edit={isEditMode}
                      onChange={setEditorContent}
                      preview={preview}
                      onPlainTextChange={setPlainText}
                      showToaster={showToaster}
                    />
                  </Col>
                </Row>
              </HaberForm>
            </DetailFields>
          </OtherPages>
          <DeleteConfirmationModal
            visible={isVisibleModal}
            cancelAction={() => {
              setIsVisibleModal(false);
              setSelectedParam(undefined);
              selectedVersionParam.current = undefined;
              setPublishVersionForModal(null); // Reset when modal is closed
            }}
            confirmAction={() => {
              if (selectedVersionParam.current) {
                handleRestore(selectedVersionParam.current);
              }
            }}
            heading={"Restore version?"}
            action={"restore"}
            onConfirmClick={(param) => {
              setSelectedParam(param);
              selectedVersionParam.current = param;
              handleRestore(selectedVersionParam.current);
            }}
            publish_version={
              publishVersionForModal
                ? publishVersionForModal
                : selectedItem?.publish_version
                ? selectedItem.publish_version
                : { "1.0.0": { status: "draft", published_at: null } }
            }
          ></DeleteConfirmationModal>
        </RightLayoutContainer>
      </Layout>

      {/* Toasters */}
      {visibleToaster && !actionToaster.error && (
        <AlertContainer
          color="green toaster"
          icon={alertGreen}
          msg={actionToaster.msg}
        />
      )}
      {visibleToaster && actionToaster.error && (
        <AlertContainer
          color="red toaster"
          icon={alertRed}
          msg={actionToaster.msg}
        />
      )}
    </>
  );
}
